import { cn } from "@/lib/utils";
import {
  faCheck,
  faClock,
  faExclamationTriangle,
  faFileText,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type ArticleStatus =
  | "draft"
  | "published"
  | "scheduled"
  | "in_progress"
  | "completed"
  | "failed";

interface ProgressBadgeProps {
  status: ArticleStatus;
  progress?: number;
  className?: string;
  showIcon?: boolean;
  size?: "sm" | "md" | "lg";
}

const statusConfig = {
  draft: {
    icon: faFileText,
    label: "Draft",
    className: "bg-gray-100 text-gray-800 border-gray-200",
  },
  published: {
    icon: faCheck,
    label: "Published",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  scheduled: {
    icon: faClock,
    label: "Scheduled",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
  in_progress: {
    icon: faSpinner,
    label: "Generating",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    animated: true,
  },
  completed: {
    icon: faCheck,
    label: "Completed",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  failed: {
    icon: faExclamationTriangle,
    label: "Failed",
    className: "bg-red-100 text-red-800 border-red-200",
  },
};

export function ProgressBadge({
  status,
  progress,
  className,
  showIcon = true,
  size = "md",
}: ProgressBadgeProps) {
  const config = statusConfig[status];

  const sizeClasses = {
    sm: "px-2 py-0.5 text-xs",
    md: "px-2.5 py-0.5 text-xs",
    lg: "px-3 py-1 text-sm",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-3 w-3",
    lg: "h-4 w-4",
  };

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full font-medium border",
        sizeClasses[size],
        config.className,
        className,
      )}
    >
      {showIcon && (
        <FontAwesomeIcon
          icon={config.icon}
          className={cn(
            iconSizes[size],
            "mr-1.5",
            "animated" in config && config.animated && "animate-spin",
          )}
        />
      )}
      {config.label}
      {status === "in_progress" && progress !== undefined && (
        <span className="ml-1">({progress}%)</span>
      )}
    </div>
  );
}

export default ProgressBadge;
